import { useState } from 'react';
import { useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import useGetTrunkType from './queries/useGetTrunkType';
import useUpdateTrunkType from './queries/useUpdateTrunkType';

import FormBase from '@/components/forms/new/FormBase';
import { LoadingIndicator } from 'components/v4';
import Alert from 'components/v4/Alert';
import AlertDescription from 'components/v4/AlertDescription';
import AlertIcon from 'components/v4/AlertIcon';
import {
    breadCrumbTrailEdit as breadCrumbTrail,
    editHeader as header,
    trunkTypesPath,
} from './constants';
import { trunkTypeSchema } from './schema';
import TrunkTypeNameField from './formFields/TrunkTypeNameField';
import HeaderValueField from './formFields/HeaderValueField';
import MinChannelsField from './formFields/MinChannelsField';
import IncSizeField from './formFields/IncSizeField';
import TeamsEnabledField from './formFields/TeamsEnabledField';
import SipEnabledField from './formFields/SipEnabledField';
import SharedField from './formFields/SharedField';

const TrunkTypesEdit = () => {
    const navigate = useNavigate();
    const { id } = useParams();
    const { darkMode } = useSelector((state) => state.settings);
    const [error, setError] = useState(null);

    const {
        data: trunkTypeData,
        isLoading: trunkTypeLoading,
        isFetching: trunkTypeFetching,
        isError: notFound,
    } = useGetTrunkType(id);

    const handleUpdateSuccess = () => {
        toast.success('Trunk type updated successfully!');
        navigate(trunkTypesPath);
    };

    const handleUpdateError = (err) => {
        console.error('Failed to update trunk type:', err);
        setError(
            err?.response?.data?.message ||
                'Failed to update trunk type. Please try again.',
        );
    };

    const { mutate: updateTrunkType, isPending: updatePending } =
        useUpdateTrunkType(handleUpdateSuccess, handleUpdateError);

    const finalFormSubmit = async (values) => {
        setError(null);

        let validated;
        try {
            validated = await trunkTypeSchema.validate(values);
        } catch (validationError) {
            setError(validationError.message);
            return;
        }

        updateTrunkType({
            id,
            name: validated.name,
            headerValue: validated.headerValue,
            minChannels: Number(validated.minChannels),
            incSize: Number(validated.incSize),
            teamsEnabled: !!values.teamsEnabled,
            sipEnabled: !!values.sipEnabled,
            shared: !!values.shared,
        });
    };

    const formFields = (errors, touched, values, setFieldValue) =>
        [
            <TrunkTypeNameField
                key="name"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <HeaderValueField
                key="headerValue"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <MinChannelsField
                key="minChannels"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <IncSizeField
                key="incSize"
                errors={errors}
                touched={touched}
                darkMode={darkMode}
            />,
            <TeamsEnabledField
                key="teamsEnabled"
                darkMode={darkMode}
                values={values}
                setFieldValue={setFieldValue}
            />,
            <SipEnabledField
                key="sipEnabled"
                darkMode={darkMode}
                values={values}
                setFieldValue={setFieldValue}
            />,
            <SharedField
                key="shared"
                darkMode={darkMode}
                values={values}
                setFieldValue={setFieldValue}
            />,
            error && (
                <Alert key="error" status="error" sx={{ margin: '25px 0' }}>
                    <AlertIcon />
                    <AlertDescription>{error}</AlertDescription>
                </Alert>
            ),
        ].filter(Boolean);

    if (trunkTypeLoading || trunkTypeFetching) {
        return <LoadingIndicator />;
    }

    if (notFound) {
        return (
            <FormBase
                breadCrumbTrail={breadCrumbTrail}
                header={header}
                initialValues={{ name: '' }}
                finalFormSubmit={async () => {}}
                hideSubmitButton
                backLink={[trunkTypesPath, { state: { index: 0 } }]}>
                {() => [
                    <p key="not-found">
                        This trunk type no longer exists. It may have already
                        been deleted.
                    </p>,
                ]}
            </FormBase>
        );
    }

    const editInitialValues = {
        name: trunkTypeData?.name || '',
        headerValue: trunkTypeData?.headerValue || '',
        minChannels: trunkTypeData?.minChannels ?? '',
        incSize: trunkTypeData?.incSize ?? '',
        teamsEnabled: !!trunkTypeData?.teamsEnabled,
        sipEnabled: !!trunkTypeData?.sipEnabled,
        shared: !!trunkTypeData?.shared,
    };

    return (
        <FormBase
            isSubmitDisabled={updatePending}
            isLoading={updatePending}
            breadCrumbTrail={breadCrumbTrail}
            header={header}
            initialValues={editInitialValues}
            finalFormSubmit={finalFormSubmit}
            backLink={[trunkTypesPath, { state: { index: 0 } }]}>
            {({ errors, touched, values, setFieldValue }) =>
                formFields(errors, touched, values, setFieldValue)
            }
        </FormBase>
    );
};

export default TrunkTypesEdit;
